/**
 * StoreGate — route guard for the store (CLA-980).
 *
 * The store is locked platform-wide until a clinic is deliberately
 * unlocked server-side (`feature_store_unlocked` — see
 * Claro.Commerce.StoreAccess). The sidebar padlock alone was cosmetic:
 * /order/* was reachable by URL, the g-o shortcut, and the command
 * palette. This gate closes those paths; the API enforces the same lock,
 * so this is presentation, not the security boundary.
 *
 * Locked clinics get a "coming soon" teaser rather than an error — the
 * store being locked is a launch state, not a failure state.
 *
 * Admins bypass, mirroring the server's admin:* bypass — Fresh-side staff
 * configure the store while it is locked.
 */

import { useAuthStore } from '@/stores/auth'
import { useSubscription } from '@/api/subscription-hooks'
import { Link } from 'react-router'
import { Lock } from 'lucide-react'
import { FreshLogo } from './FreshLogo'
import { StoreOnboardingApply } from './StoreOnboardingApply'
import { regionalText } from '@/lib/regional-copy'

// The real top-level catalogue categories (product_categories seed) —
// shown as a static teaser of what's coming, no network call needed.
const teaserCategories = ['Injectables', 'Skincare', 'Consumables', 'Devices & equipment']

/** One skeleton "product card" in the decorative fan. */
function FanCard({ className, front }: { className: string; front?: boolean }) {
  return (
    <div
      className={`absolute left-1/2 top-1/2 w-24 h-32 -ml-12 -mt-16 rounded-xl bg-surface border border-border p-2.5 ${
        front ? 'shadow-lg' : 'shadow-sm'
      } ${className}`}
    >
      <div className="h-14 rounded-md bg-gradient-to-br from-claro-50 to-claro-100" />
      <div className="mt-2.5 h-2 w-3/4 rounded-full bg-surface-tertiary" />
      <div className="mt-1.5 h-2 w-1/2 rounded-full bg-surface-tertiary" />
      {front && (
        <div className="absolute -top-2.5 -right-2.5 w-7 h-7 rounded-full bg-claro-600 shadow-md flex items-center justify-center">
          <Lock className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
        </div>
      )}
    </div>
  )
}

export function StoreGate({ children }: { children: React.ReactNode }) {
  // Mirror the SERVER's admin bypass exactly: bare `*` (superadmin) or the
  // literal `admin:*`. The store's `isAdmin` (`perms.some(startsWith('admin:'))`)
  // is far broader — every clinic owner holds named grants like
  // `admin:settings`, so using it here bypassed the teaser for exactly the
  // people the apply form is FOR, dropping them into a storefront whose API
  // calls the locked backend then rejects (CLA-993).
  const isPlatformAdmin = useAuthStore(s => s.hasPermission)('admin:*')
  const canManageClinic = useAuthStore(s => s.canManageClinic)()
  const { data: subscription } = useSubscription()

  if (isPlatformAdmin) return <>{children}</>

  // Fail-closed once loaded; render nothing during the initial load so a
  // locked clinic never flashes the storefront.
  if (subscription === undefined) return null
  if (subscription.store_unlocked) return <>{children}</>

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-md text-center">
        {/* Storefront-with-the-lights-off: a fan of skeleton product cards */}
        <div
          aria-hidden="true"
          className="relative h-40 mb-10 animate-rise-in motion-reduce:animate-none"
        >
          <FanCard className="-rotate-12 -translate-x-16 translate-y-1 opacity-60" />
          <FanCard className="rotate-12 translate-x-16 translate-y-1 opacity-60" />
          <FanCard front className="-translate-y-0.5" />
        </div>

        <p
          className="text-[11px] font-semibold tracking-[0.18em] uppercase text-claro-600 mb-3 animate-rise-in motion-reduce:animate-none"
          style={{ animationDelay: '80ms' }}
        >
          Store
        </p>
        <h1
          className="text-2xl font-semibold text-ink tracking-tight mb-3 animate-rise-in motion-reduce:animate-none"
          style={{ animationDelay: '140ms' }}
        >
          The store is coming
        </h1>
        <p
          className="text-sm text-ink-secondary leading-relaxed mb-6 animate-rise-in motion-reduce:animate-none"
          style={{ animationDelay: '200ms' }}
        >
          {regionalText('Stock and supplies for your clinic — ordered from your suppliers, delivered to your door, all in one place.')}
        </p>

        <div
          className="flex flex-wrap justify-center gap-2 mb-8 animate-rise-in motion-reduce:animate-none"
          style={{ animationDelay: '260ms' }}
        >
          {teaserCategories.map(name => (
            <span
              key={name}
              className="px-3 py-1 rounded-full bg-surface-secondary border border-border-subtle text-xs text-ink-tertiary"
            >
              {name}
            </span>
          ))}
        </div>

        {canManageClinic ? (
          // CLA-993: clinic managers apply for access right here.
          <div className="animate-rise-in motion-reduce:animate-none" style={{ animationDelay: '320ms' }}>
            <StoreOnboardingApply />
          </div>
        ) : (
          <p
            className="text-xs text-ink-tertiary mb-4 animate-rise-in motion-reduce:animate-none"
            style={{ animationDelay: '320ms' }}
          >
            {regionalText('Ordering isn\'t open for your clinic yet — you\'ll see it unlock right here when it\'s ready.')}
          </p>
        )}

        <p
          className="flex items-center justify-center gap-1.5 text-xs text-ink-faint mb-8 animate-rise-in motion-reduce:animate-none"
          style={{ animationDelay: '360ms' }}
        >
          Powered by <FreshLogo className="h-4 w-auto text-ink-tertiary" />
        </p>

        <Link
          to="/"
          className="text-sm font-medium animate-rise-in motion-reduce:animate-none link"
          style={{ animationDelay: '400ms' }}
        >
          Back to notes
        </Link>
      </div>
    </div>
  )
}
